import React, { useState, useEffect, useRef, useMemo } from "react";
import { acquireBackendApiToken } from "../../utils/msalTokenService";
import { THRESHOLD_DEFAULTS, ALLOWED_THRESHOLD_KEYS, type ThresholdOverrides } from "../../constants/thresholdDefaults";
import { getConfiguredTimezone, setConfiguredTimezone, clearConfiguredTimezone, getBrowserTimezone, getAvailableTimezones } from "../../utils/formatDate";

interface MaintenanceModeStatus {
  enabled: boolean;
  message: string | null;
}

// Use objects API for settings (different from audit API)
const OBJECTS_API_BASE_URL = import.meta.env.VITE_DEALS_API_BASE_URL;

export default function SettingsView(): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Timezone state (localStorage-only)
  const [selectedTimezone, setSelectedTimezone] = useState<string>(getConfiguredTimezone());
  const [timezoneSearch, setTimezoneSearch] = useState('');
  const browserTimezone = getBrowserTimezone();
  const allTimezones = useMemo(() => getAvailableTimezones(), []);
  const filteredTimezones = useMemo(
    () => timezoneSearch
      ? allTimezones.filter(tz => tz.toLowerCase().includes(timezoneSearch.toLowerCase()))
      : allTimezones,
    [timezoneSearch, allTimezones]
  );

  const handleTimezoneChange = (timezone: string) => {
    setSelectedTimezone(timezone);
    setConfiguredTimezone(timezone);
  };

  // Threshold overrides state (localStorage-only, no API)
  const [thresholdOverridesEnabled, setThresholdOverridesEnabled] = useState(false);
  const [thresholdOverrides, setThresholdOverrides] = useState<ThresholdOverrides>({ ...THRESHOLD_DEFAULTS });

  // Load threshold overrides from localStorage on mount (with validation)
  useEffect(() => {
    try {
      const enabled = localStorage.getItem('dealos.thresholdOverridesEnabled') === 'true';
      setThresholdOverridesEnabled(enabled);
      const stored = localStorage.getItem('dealos.thresholdOverrides');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          const validated: ThresholdOverrides = { ...THRESHOLD_DEFAULTS };
          for (const key of ALLOWED_THRESHOLD_KEYS) {
            if (key in parsed) {
              const num = Number(parsed[key]);
              if (Number.isFinite(num)) {
                validated[key] = num;
              }
            }
          }
          setThresholdOverrides(validated);
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, []);

  const saveThresholdOverrides = (overrides: ThresholdOverrides) => {
    setThresholdOverrides(overrides);
    localStorage.setItem('dealos.thresholdOverrides', JSON.stringify(overrides));
  };

  const toggleThresholdOverrides = (enabled: boolean) => {
    setThresholdOverridesEnabled(enabled);
    localStorage.setItem('dealos.thresholdOverridesEnabled', String(enabled));
  };

  const resetThresholdDefaults = () => {
    const defaults = { ...THRESHOLD_DEFAULTS };
    setThresholdOverrides(defaults);
    localStorage.setItem('dealos.thresholdOverrides', JSON.stringify(defaults));
  };

  const updateThreshold = (key: keyof ThresholdOverrides, value: string) => {
    const numValue = key === 'page_size' ? parseInt(value, 10) : parseFloat(value);
    if (!isNaN(numValue)) {
      saveThresholdOverrides({ ...thresholdOverrides, [key]: numValue });
    }
  };

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // Maintenance mode state
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");

  // Fetch current maintenance mode status
  useEffect(() => {
    fetchMaintenanceMode();
  }, []);

  const fetchMaintenanceMode = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${OBJECTS_API_BASE_URL}/api/objects/settings/maintenance-mode`);

      if (!response.ok) {
        throw new Error(`Failed to fetch maintenance mode: ${response.status}`);
      }

      const status: MaintenanceModeStatus = await response.json();
      setMaintenanceEnabled(status.enabled);
      setMaintenanceMessage(status.message || "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Get authenticated headers
      const token = await acquireBackendApiToken();

      const response = await fetch(`${OBJECTS_API_BASE_URL}/api/objects/settings/maintenance-mode`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          enabled: maintenanceEnabled,
          message: maintenanceMessage || null,
        }),
      });

      if (response.status === 403) {
        throw new Error("Access denied. You must be an admin to change settings.");
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save settings: ${response.status} - ${errorText}`);
      }

      const status: MaintenanceModeStatus = await response.json();
      setMaintenanceEnabled(status.enabled);
      setMaintenanceMessage(status.message || "");
      setSuccess("Settings saved successfully!");

      // Clear success message after 3 seconds
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      successTimeoutRef.current = setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Alert */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Mode Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Maintenance Mode</h3>
          <p className="mt-1 text-sm text-gray-500">
            When enabled, non-admin users will see a maintenance page instead of the application.
          </p>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="maintenance-toggle" className="text-sm font-medium text-gray-900">
                Enable Maintenance Mode
              </label>
              <p className="text-sm text-gray-500">
                {maintenanceEnabled
                  ? "Maintenance mode is currently ON. Non-admin users cannot access the application."
                  : "Maintenance mode is currently OFF. All users can access the application."}
              </p>
            </div>
            <button
              id="maintenance-toggle"
              type="button"
              onClick={() => setMaintenanceEnabled(!maintenanceEnabled)}
              className={`${
                maintenanceEnabled ? "bg-red-600" : "bg-gray-200"
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
              role="switch"
              aria-checked={maintenanceEnabled}
            >
              <span className="sr-only">Toggle maintenance mode</span>
              <span
                aria-hidden="true"
                className={`${
                  maintenanceEnabled ? "translate-x-5" : "translate-x-0"
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>

          {/* Warning when enabled */}
          {maintenanceEnabled && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <strong>Warning:</strong> Saving with maintenance mode enabled will immediately prevent non-admin users from accessing the application.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Maintenance Message */}
          <div>
            <label htmlFor="maintenance-message" className="block text-sm font-medium text-gray-700">
              Maintenance Message (Optional)
            </label>
            <p className="text-sm text-gray-500 mb-2">
              This message will be shown to users on the maintenance page.
            </p>
            <textarea
              id="maintenance-message"
              rows={3}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="We're currently performing scheduled maintenance. Please check back shortly."
              value={maintenanceMessage}
              onChange={(e) => setMaintenanceMessage(e.target.value)}
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
              saving
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            }`}
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </>
            ) : (
              "Save Settings"
            )}
          </button>
        </div>
      </div>

      {/* Search Threshold Overrides Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Search Threshold Overrides</h3>
          <p className="mt-1 text-sm text-gray-500">
            Override MCP search thresholds. Non-default values are included with each message sent to the backend.
          </p>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <span id="threshold-toggle-label" className="text-sm font-medium text-gray-900">
                Send Threshold Overrides
              </span>
              <p className="text-sm text-gray-500">
                {thresholdOverridesEnabled
                  ? "Overrides are active. Non-default values will be sent with each message."
                  : "Overrides are disabled. MCP servers will use their default thresholds."}
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleThresholdOverrides(!thresholdOverridesEnabled)}
              className={`${
                thresholdOverridesEnabled ? "bg-blue-600" : "bg-gray-200"
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
              role="switch"
              aria-checked={thresholdOverridesEnabled}
              aria-labelledby="threshold-toggle-label"
            >
              <span
                aria-hidden="true"
                className={`${
                  thresholdOverridesEnabled ? "translate-x-5" : "translate-x-0"
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>

          {/* Deal Search Thresholds */}
          <div className={thresholdOverridesEnabled ? "" : "opacity-50"}>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Deal Search Thresholds</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="score_floor" className="block text-xs font-medium text-gray-600">
                  Score Floor (default: {THRESHOLD_DEFAULTS.score_floor})
                </label>
                <input
                  id="score_floor"
                  type="number"
                  min={0} max={1} step={0.01}
                  disabled={!thresholdOverridesEnabled}
                  value={thresholdOverrides.score_floor}
                  onChange={(e) => updateThreshold('score_floor', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-400">Min column score for mean calculation</p>
              </div>
              <div>
                <label htmlFor="result_max_threshold" className="block text-xs font-medium text-gray-600">
                  Max Threshold (default: {THRESHOLD_DEFAULTS.result_max_threshold})
                </label>
                <input
                  id="result_max_threshold"
                  type="number"
                  min={0} max={1} step={0.01}
                  disabled={!thresholdOverridesEnabled}
                  value={thresholdOverrides.result_max_threshold}
                  onChange={(e) => updateThreshold('result_max_threshold', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-400">Include deal if max score exceeds this</p>
              </div>
              <div>
                <label htmlFor="result_mean_threshold" className="block text-xs font-medium text-gray-600">
                  Mean Threshold (default: {THRESHOLD_DEFAULTS.result_mean_threshold})
                </label>
                <input
                  id="result_mean_threshold"
                  type="number"
                  min={0} max={1} step={0.01}
                  disabled={!thresholdOverridesEnabled}
                  value={thresholdOverrides.result_mean_threshold}
                  onChange={(e) => updateThreshold('result_mean_threshold', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-400">Include deal if mean above-floor score exceeds this</p>
              </div>
            </div>
          </div>

          {/* Document Search Thresholds */}
          <div className={thresholdOverridesEnabled ? "" : "opacity-50"}>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Document Search Thresholds</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="page_size" className="block text-xs font-medium text-gray-600">
                  Page Size (default: {THRESHOLD_DEFAULTS.page_size})
                </label>
                <input
                  id="page_size"
                  type="number"
                  min={1} max={200} step={1}
                  disabled={!thresholdOverridesEnabled}
                  value={thresholdOverrides.page_size}
                  onChange={(e) => updateThreshold('page_size', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-400">Results per retrieval page</p>
              </div>
              <div>
                <label htmlFor="similarity_threshold" className="block text-xs font-medium text-gray-600">
                  Similarity Threshold (default: {THRESHOLD_DEFAULTS.similarity_threshold})
                </label>
                <input
                  id="similarity_threshold"
                  type="number"
                  min={0} max={1} step={0.01}
                  disabled={!thresholdOverridesEnabled}
                  value={thresholdOverrides.similarity_threshold}
                  onChange={(e) => updateThreshold('similarity_threshold', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-400">Minimum chunk similarity score</p>
              </div>
              <div>
                <label htmlFor="vector_similarity_weight" className="block text-xs font-medium text-gray-600">
                  Vector Weight (default: {THRESHOLD_DEFAULTS.vector_similarity_weight})
                </label>
                <input
                  id="vector_similarity_weight"
                  type="number"
                  min={0} max={1} step={0.01}
                  disabled={!thresholdOverridesEnabled}
                  value={thresholdOverrides.vector_similarity_weight}
                  onChange={(e) => updateThreshold('vector_similarity_weight', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-400">Weight of vector vs keyword similarity</p>
              </div>
            </div>
          </div>
        </div>

        {/* Reset to Defaults Button */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={resetThresholdDefaults}
            disabled={!thresholdOverridesEnabled}
            className={`inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm ${
              thresholdOverridesEnabled
                ? "text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                : "text-gray-400 bg-gray-100 cursor-not-allowed"
            }`}
          >
            Reset to Defaults
          </button>
        </div>
      </div>

      {/* Display Settings Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Display Settings</h3>
          <p className="mt-1 text-sm text-gray-500">
            Configure how dates and times are displayed across the admin interface.
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label htmlFor="timezone-select" className="block text-sm font-medium text-gray-700">
              Timezone
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Your browser detected: <span className="font-mono text-gray-700">{browserTimezone}</span>
            </p>

            <input
              id="timezone-search"
              type="text"
              aria-label="Search timezones"
              value={timezoneSearch}
              onChange={(e) => setTimezoneSearch(e.target.value)}
              placeholder="Search timezones (e.g. London, New_York, Tokyo)"
              className="mb-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />

            <div
              className="block w-full h-48 overflow-y-auto rounded-md border border-gray-300 shadow-sm bg-white sm:text-sm"
            >
              {filteredTimezones.map((tz) => (
                <button
                  key={tz}
                  type="button"
                  onClick={() => {
                    handleTimezoneChange(tz);
                    setTimezoneSearch('');
                  }}
                  className={`block w-full text-left px-3 py-1.5 text-sm ${
                    tz === selectedTimezone
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-900 hover:bg-blue-50'
                  }`}
                >
                  {tz}{tz === browserTimezone ? ' (browser default)' : ''}
                </button>
              ))}
              {filteredTimezones.length === 0 && (
                <p className="px-3 py-2 text-sm text-gray-400">No timezones match your search</p>
              )}
            </div>

            <p className="mt-2 text-xs text-gray-500">
              Current time in selected timezone:{' '}
              <span className="font-mono">
                {new Date().toLocaleString(undefined, {
                  timeZone: selectedTimezone,
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </span>
            </p>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={() => {
              clearConfiguredTimezone();
              setSelectedTimezone(browserTimezone);
              setTimezoneSearch('');
            }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Reset to Browser Default
          </button>
        </div>
      </div>
    </div>
  );
}
